/* global bootstrap */
'use strict';

(function () {
  function loadHtml2Canvas (src) {
    return new Promise(function (resolve, reject) {
      if (window.html2canvas) {
        resolve(window.html2canvas);
        return;
      }
      if (!src) {
        reject(new Error('html2canvas src not configured'));
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.onload = function () { resolve(window.html2canvas); };
      script.onerror = function () { reject(new Error('html2canvas not available')); };
      document.head.appendChild(script);
    });
  }

  // html2canvas does not implement `object-fit`: it paints every <img> stretched
  // across its full layout box. Any image laid out as `contain` or `cover` therefore
  // comes out distorted — product tiles, the gallery slider and its thumbnails alike,
  // since `.splide__slide img` is width:100%/height:100%/object-fit:contain.
  //
  // The workaround is to hand html2canvas something it does honour: a plain <div> of
  // the image's measured box, painted with the same bitmap as a background. CSS
  // `background-size: contain|cover` and `background-position` reproduce `object-fit`
  // and `object-position` exactly, so the clone renders what the browser shows.
  //
  // Measuring has to happen on the live document (the clone is detached and has no
  // layout), hence the tag-then-swap split. Tags are removed again in `finally`.
  function tagFittedImages () {
    document.querySelectorAll('img').forEach(function (img) {
      const style = window.getComputedStyle(img);
      if (style.objectFit !== 'contain' && style.objectFit !== 'cover') { return; }

      // Skip what cannot be painted: images in hidden tabs (no box) and slides the
      // lazy-loader has not resolved yet (no source).
      const rect = img.getBoundingClientRect();
      const source = img.currentSrc || img.src;
      if (!rect.width || !rect.height || !source) { return; }

      img.setAttribute('data-h2c-w', rect.width);
      img.setAttribute('data-h2c-h', rect.height);
      img.setAttribute('data-h2c-fit', style.objectFit);
      img.setAttribute('data-h2c-pos', style.objectPosition);
      // Resolved here rather than in the clone: `src` alone would lose the srcset
      // candidate the browser actually picked.
      img.setAttribute('data-h2c-src', source);
    });
  }

  function untagFittedImages () {
    document.querySelectorAll('img[data-h2c-fit]').forEach(function (img) {
      img.removeAttribute('data-h2c-w');
      img.removeAttribute('data-h2c-h');
      img.removeAttribute('data-h2c-fit');
      img.removeAttribute('data-h2c-pos');
      img.removeAttribute('data-h2c-src');
    });
  }

  function replaceFittedImages (clonedDoc) {
    clonedDoc.querySelectorAll('img[data-h2c-fit]').forEach(function (img) {
      const div = clonedDoc.createElement('div');
      div.style.cssText =
        'width:' + img.getAttribute('data-h2c-w') + 'px;' +
        'height:' + img.getAttribute('data-h2c-h') + 'px;' +
        'background-image:url(' + JSON.stringify(img.getAttribute('data-h2c-src')) + ');' +
        'background-size:' + img.getAttribute('data-h2c-fit') + ';' +
        'background-position:' + img.getAttribute('data-h2c-pos') + ';' +
        'background-repeat:no-repeat;';
      img.parentNode.replaceChild(div, img);
    });
  }

  function captureScreenshot (src, modalEl) {
    tagFittedImages();

    return loadHtml2Canvas(src).then(function (html2canvas) {
      return html2canvas(document.body, {
        useCORS: true,
        allowTaint: false,
        scale: 0.5,
        logging: false,
        ignoreElements: function (el) {
          return el === modalEl || el.classList.contains('modal-backdrop');
        },
        onclone: replaceFittedImages,
      }).then(function (canvas) {
        return canvas.toDataURL('image/jpeg', 0.7);
      });
    }).finally(untagFittedImages);
  }

  // ── UI helpers ──────────────────────────────────────────────────────────────

  function showForm (modalEl) {
    modalEl.querySelector('.product-report-fields').classList.remove('d-none');
    modalEl.querySelector('.product-report-cancel-btn').classList.remove('d-none');
    modalEl.querySelector('.product-report-submit').classList.remove('d-none');
    modalEl.querySelector('.product-report-close-btn').classList.add('d-none');
  }

  function showResult (form, success) {
    const modalEl = form.closest('.modal');
    const statusEl = form.querySelector('.product-report-status');

    modalEl.querySelector('.product-report-fields').classList.add('d-none');
    modalEl.querySelector('.product-report-cancel-btn').classList.add('d-none');
    modalEl.querySelector('.product-report-submit').classList.add('d-none');
    modalEl.querySelector('.product-report-close-btn').classList.remove('d-none');

    if (success) {
      statusEl.className = 'product-report-status alert alert-success';
      statusEl.textContent = form.dataset.msgSuccess || 'Vielen Dank! Ihre Meldung wurde gesendet.';
    } else {
      statusEl.className = 'product-report-status alert alert-danger';
      statusEl.textContent = form.dataset.msgError || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
    }
  }

  function resetModal (modalEl) {
    const form = modalEl.querySelector('.product-report-form');
    form.querySelector('textarea').value = '';
    const emailInput = form.querySelector('input[type="email"]');
    if (emailInput) { emailInput.value = ''; }
    const areaSelect = form.querySelector('select[name*="[area]"]');
    if (areaSelect) { areaSelect.selectedIndex = 0; }
    form.querySelector('.product-report-screenshot').value = '';
    const statusEl = form.querySelector('.product-report-status');
    statusEl.className = 'product-report-status alert d-none';
    statusEl.textContent = '';
    const captchaEl = form.querySelector('.frc-captcha');
    if (captchaEl && captchaEl.frcWidget) { captchaEl.frcWidget.reset(); }
    showForm(modalEl);
  }

  // ── Submit ───────────────────────────────────────────────────────────────────

  function doSubmit (form) {
    const actionUrl = form.dataset.actionUrl;
    const submitBtn = form.querySelector('.product-report-submit');
    const submitText = form.querySelector('.product-report-submit-text');
    const submitLoading = form.querySelector('.product-report-submit-loading');

    submitBtn.disabled = true;
    submitText.classList.add('d-none');
    submitLoading.classList.remove('d-none');

    form.querySelector('.product-report-pageurl').value = window.location.href;
    form.querySelector('.product-report-useragent').value = navigator.userAgent;
    form.querySelector('.product-report-screenresolution').value = window.screen.width + 'x' + window.screen.height;

    fetch(actionUrl, {
      method: 'POST',
      body: new FormData(form),
    })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        submitBtn.disabled = false;
        submitText.classList.remove('d-none');
        submitLoading.classList.add('d-none');
        showResult(form, !!data.success);
      })
      .catch(function () {
        submitBtn.disabled = false;
        submitText.classList.remove('d-none');
        submitLoading.classList.add('d-none');
        showResult(form, false);
      });
  }

  // ── Init ─────────────────────────────────────────────────────────────────────

  function initBtn (btn) {
    const modalId = btn.dataset.modalId;
    if (!modalId) { return; }

    const modalEl = document.getElementById(modalId);
    if (!modalEl) { return; }

    const modal = bootstrap.Modal.getOrCreateInstance(modalEl);

    modalEl.addEventListener('hidden.bs.modal', function () {
      resetModal(modalEl);
    });

    btn.addEventListener('click', function () {
      modal.show();
    });
  }

  function initForm (form) {
    const actionUrl = form.dataset.actionUrl;

    // Resolve html2canvas src from the trigger button
    const modalEl = form.closest('.modal');
    const triggerBtn = modalEl
      ? document.querySelector('[data-modal-id="' + modalEl.id + '"]')
      : null;
    const screenshotSrc = triggerBtn ? triggerBtn.dataset.html2canvasSrc : null;

    if (!actionUrl) { return; }

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      const screenshotCheck = form.querySelector('.product-report-screenshot-check');
      const screenshotInput = form.querySelector('.product-report-screenshot');

      if (screenshotCheck && screenshotCheck.checked) {
        const submitBtn = form.querySelector('.product-report-submit');
        const submitText = form.querySelector('.product-report-submit-text');
        const submitLoading = form.querySelector('.product-report-submit-loading');
        submitBtn.disabled = true;
        submitText.classList.add('d-none');
        submitLoading.classList.remove('d-none');
        captureScreenshot(screenshotSrc, modalEl)
          .then(function (dataUrl) { screenshotInput.value = dataUrl; })
          .catch(function () { screenshotInput.value = ''; })
          .finally(function () {
            submitBtn.disabled = false;
            submitText.classList.remove('d-none');
            submitLoading.classList.add('d-none');
            doSubmit(form);
          });
      } else {
        screenshotInput.value = '';
        doSubmit(form);
      }
    });
  }

  function init () {
    document.querySelectorAll('.product-report-btn').forEach(initBtn);
    document.querySelectorAll('.product-report-form').forEach(initForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
